<template>
  <b-modal
    id="save-search-modal"
    :title="view === 'list' ? 'Saved Searches' : 'New Saved Search'"
    cancel-variant="outline-secondary"
    centered
    hide-footer
    :size="view === 'list' ? 'md' : 'lg'"
    @hidden="view = 'list'"
  >
    <!-- ── List view: all saved searches for this contact ──────────────── -->
    <template v-if="view === 'list'">
      <div v-if="listLoading" class="text-center py-3">
        <b-spinner small variant="primary" />
      </div>

      <div v-else-if="list.length === 0" class="text-muted small text-center py-3">
        No saved searches yet for this contact.
      </div>

      <div v-else class="saved-search-list">
        <div v-for="item in list" :key="item.id" class="saved-search-item">
          <div class="saved-search-item__body">
            <div class="saved-search-item__label">{{ displayLabel(item) }}</div>
            <div class="saved-search-item__meta">
              {{ displaySummary(item) }}<span v-if="displaySummary(item)"> · </span>{{ formatDate(item.created_at) }}
              <span v-if="!item.notify_email" class="saved-search-item__paused">· Paused</span>
            </div>
          </div>
          <button class="saved-search-item__delete" title="Delete" @click="confirmRemove(item)">
            <feather-icon icon="Trash2Icon" size="15" />
          </button>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-1" style="gap:8px">
        <b-button variant="outline-secondary" size="sm" @click="$bvModal.hide('save-search-modal')">Close</b-button>
        <b-button variant="primary" size="sm" @click="showCreateForm">+ Add saved search</b-button>
      </div>
    </template>

    <!-- ── Create view: the save-search form ────────────────────────────── -->
    <template v-else>
      <div class="ss-form-scroll">
      <p class="text-muted small mb-1">
        The customer will get emailed whenever a new or updated listing matches these criteria.
      </p>

      <b-form-group label="Customer email" label-for="ss-email">
        <b-form-input
          id="ss-email"
          v-model="savedSearchForm.email"
          type="email"
          placeholder="customer@email.com"
        />
      </b-form-group>

      <b-form-group label="Label (optional)" label-for="ss-label">
        <b-form-input
          id="ss-label"
          v-model="savedSearchForm.label"
          placeholder="e.g. 2BR condo Thonglor under 30k"
        />
      </b-form-group>

      <b-form-group label="Place / Project (optional)" label-for="ss-place">
        <div class="position-relative">
          <b-form-input
            id="ss-place"
            v-model="savedSearchForm.placeName"
            placeholder="e.g. The Line Asoke"
            autocomplete="off"
            @input="onPlaceInput"
            @blur="hidePlaceSugg"
          />
          <ul
            v-if="showPlaceSugg && placeSuggestions.length"
            class="ss-suggest-list list-group position-absolute shadow-sm"
          >
            <li
              v-for="item in placeSuggestions"
              :key="item.id"
              class="list-group-item list-group-item-action py-1"
              @mousedown.prevent="selectPlace(item)"
            >{{ localeName(item.name) }}</li>
          </ul>
        </div>
      </b-form-group>

      <b-form-group label="Action">
        <div class="ss-toggle-group">
          <button
            v-for="opt in actionOptions" :key="opt.value"
            type="button"
            class="ss-toggle-btn"
            :class="{ 'ss-toggle-btn--active': savedSearchForm.action === opt.value }"
            @click="savedSearchForm.action = opt.value"
          >{{ opt.text }}</button>
        </div>
      </b-form-group>

      <b-form-group label="Property type">
        <v-select
          v-model="savedSearchForm.propertyType"
          :options="propertyTypeOptions"
          label="name"
          :reduce="o => o.id"
          placeholder="Any"
          append-to-body
        />
      </b-form-group>

      <b-form-group label="Bedrooms">
        <div class="ss-toggle-group">
          <button
            v-for="label in bedroomLabels" :key="label"
            type="button"
            class="ss-toggle-btn"
            :class="{ 'ss-toggle-btn--active': savedSearchForm.bedrooms.includes(label) }"
            @click="toggleSaveSearchBedroom(label)"
          >{{ label }}</button>
        </div>
      </b-form-group>

      <b-form-group label="Furniture">
        <v-select
          v-model="savedSearchForm.furniture"
          multiple
          :options="furnitureOptions"
          label="name"
          :reduce="o => o.id"
          placeholder="Any"
          append-to-body
        />
      </b-form-group>

      <div class="d-flex" style="gap:10px">
        <b-form-group label="Min price (฿)" class="flex-fill">
          <b-form-input v-model.number="savedSearchForm.priceMin" type="number" min="0" />
        </b-form-group>
        <b-form-group label="Max price (฿)" class="flex-fill">
          <b-form-input v-model.number="savedSearchForm.priceMax" type="number" min="0" />
        </b-form-group>
      </div>

      <div class="d-flex" style="gap:10px">
        <b-form-group label="Min SQM" class="flex-fill">
          <b-form-input v-model.number="savedSearchForm.sqmMin" type="number" min="0" />
        </b-form-group>
        <b-form-group label="Max SQM" class="flex-fill">
          <b-form-input v-model.number="savedSearchForm.sqmMax" type="number" min="0" />
        </b-form-group>
      </div>

      <b-form-group label="Zone">
        <v-select
          v-model="savedSearchForm.zones"
          multiple
          :options="zoneOptions"
          label="name"
          :reduce="o => o.id"
          placeholder="All zones"
          append-to-body
        />
      </b-form-group>

      <b-form-group label="Transport">
        <b-tabs small class="ss-transit-tabs">
          <b-tab title="BTS">
            <v-select v-model="savedSearchForm.bts" multiple :options="btsOptions" label="name" :reduce="o => o.id" placeholder="BTS stations..." append-to-body />
          </b-tab>
          <b-tab title="MRT">
            <v-select v-model="savedSearchForm.mrt" multiple :options="mrtOptions" label="name" :reduce="o => o.id" placeholder="MRT stations..." append-to-body />
          </b-tab>
          <b-tab title="ARL">
            <v-select v-model="savedSearchForm.arl" multiple :options="arlOptions" label="name" :reduce="o => o.id" placeholder="ARL stations..." append-to-body />
          </b-tab>
        </b-tabs>
      </b-form-group>
      </div>

      <div class="d-flex justify-content-end mt-1" style="gap:8px">
        <b-button variant="outline-secondary" size="sm" @click="view = 'list'">Back</b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!savedSearchForm.email.trim() || savingSearch"
          @click="createSavedSearch"
        >
          <b-spinner v-if="savingSearch" small />
          <span v-else>Save</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from '@axios'
import {
  BModal, BButton, BFormGroup, BFormInput, BSpinner, BTabs, BTab, VBModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  name: 'SaveSearchModal',
  components: {
    BModal, BButton, BFormGroup, BFormInput, BSpinner, BTabs, BTab, vSelect,
  },
  directives: { 'b-modal': VBModal },

  props: {
    selectedUser: { type: Object, default: null },
  },

  data() {
    return {
      view: 'list',
      list: [],
      listLoading: false,

      savingSearch: false,
      referenceDataLoaded: false,
      propertyTypeOptions: [],
      furnitureOptions: [],
      zoneOptions: [],
      btsOptions: [],
      mrtOptions: [],
      arlOptions: [],
      bedroomLabels: ['Studio', '1', '2', '3', '4+'],
      actionOptions: [{ value: '2,4', text: 'Rent' }, { value: '2,3', text: 'Sale' }],
      savedSearchForm: this.emptySavedSearchForm(),

      placeSuggestions: [],
      showPlaceSugg: false,
      placeDebounce: null,
      placeRequestSeq: 0,
    }
  },

  mounted() {
    document.addEventListener('mousedown', this.onOutsideSelectClick)
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutsideSelectClick)
  },

  methods: {
    // v-select dropdowns here use append-to-body, so they render as direct
    // children of <body> instead of inside this component — a click outside
    // both the select toggle and the (relocated) dropdown menu should close it.
    onOutsideSelectClick(e) {
      const active = document.activeElement
      if (!active || !active.classList || !active.classList.contains('vs__search')) return
      if (e.target.closest('.v-select') || e.target.closest('.vs__dropdown-menu')) return
      active.blur()
    },

    emptySavedSearchForm() {
      return {
        email: '',
        label: '',
        action: '2,4',
        propertyType: null,
        bedrooms: [],
        furniture: [],
        priceMin: null,
        priceMax: null,
        sqmMin: null,
        sqmMax: null,
        zones: [],
        bts: [],
        mrt: [],
        arl: [],
        placeId: null,
        placeName: '',
      }
    },

    open() {
      if (!this.selectedUser) return
      this.view = 'list'
      this.loadList()
      this.$bvModal.show('save-search-modal')
    },

    async loadList() {
      this.listLoading = true
      try {
        const { data } = await axios.get(`users/v1/saved-search/agent-list/${this.selectedUser.user_id}`)
        this.list = data.data || []
      } catch (e) {
        this.$emit('error', 'Failed to load saved searches: ' + (e.response?.data?.message || e.message))
      } finally {
        this.listLoading = false
      }
    },

    showCreateForm() {
      this.savedSearchForm = this.emptySavedSearchForm()
      this.placeSuggestions = []
      this.showPlaceSugg = false
      if (!this.referenceDataLoaded) this.loadReferenceData()
      this.view = 'create'
    },

    async loadReferenceData() {
      this.referenceDataLoaded = true
      await Promise.all([
        this.loadPropertyTypes(),
        this.loadFurnitureOptions(),
        this.loadZoneOptions(),
        this.loadTransitOptions(),
      ])
    },

    async loadPropertyTypes() {
      try {
        const { data } = await axios.get('place/v1/type')
        this.propertyTypeOptions = [{ id: null, name: 'All' }, ...(data.data || []).sort((a, b) => a.seq - b.seq)]
      } catch (e) {
        console.warn('Failed to load property types:', e.message)
      }
    },

    async loadFurnitureOptions() {
      try {
        const { data } = await axios.get('furnituretype/v1/list')
        this.furnitureOptions = data?.data || []
      } catch (e) {
        console.warn('Failed to load furniture types:', e.message)
      }
    },

    async loadZoneOptions() {
      try {
        const { data } = await axios.post('zone/v1/lists', { perpage: 2000, offset: 0 })
        this.zoneOptions = (data?.data || []).map(z => ({ id: z.id, name: this.localeName(z.name) }))
      } catch (e) {
        console.warn('Failed to load zones:', e.message)
      }
    },

    async loadTransitOptions() {
      try {
        const [bts, mrt, arl] = await Promise.all([
          axios.get('transit/v1/listtype/bts'),
          axios.get('transit/v1/listtype/mrt'),
          axios.post('transit/v1/lists', { type_code: 'airport' }),
        ])
        this.btsOptions = bts.data?.data || []
        this.mrtOptions = mrt.data?.data || []
        this.arlOptions = (arl.data?.data || []).map(a => ({ id: a.id, name: this.localeName(a.name) }))
      } catch (e) {
        console.warn('Failed to load transit options:', e.message)
      }
    },

    localeName(nameObj) {
      if (!nameObj) return ''
      if (typeof nameObj === 'string') return nameObj
      return nameObj.en || nameObj.th || ''
    },

    onPlaceInput() {
      const val = this.savedSearchForm.placeName
      this.savedSearchForm.placeId = null
      clearTimeout(this.placeDebounce)
      if (!val || val.trim().length < 2) { this.placeSuggestions = []; this.showPlaceSugg = false; return }
      this.placeRequestSeq += 1
      const seq = this.placeRequestSeq
      this.placeDebounce = setTimeout(async () => {
        try {
          const { data } = await axios.post('/cache-master/v1/place-filter', { keyword: val.trim() })
          if (seq !== this.placeRequestSeq) return
          this.placeSuggestions = data.data || []
          this.showPlaceSugg = this.placeSuggestions.length > 0
        } catch {
          if (seq === this.placeRequestSeq) this.placeSuggestions = []
        }
      }, 300)
    },

    selectPlace(item) {
      this.savedSearchForm.placeId = item.id
      this.savedSearchForm.placeName = this.localeName(item.name)
      this.placeSuggestions = []
      this.showPlaceSugg = false
    },

    hidePlaceSugg() {
      setTimeout(() => { this.showPlaceSugg = false }, 160)
    },

    toggleSaveSearchBedroom(label) {
      const i = this.savedSearchForm.bedrooms.indexOf(label)
      if (i === -1) this.savedSearchForm.bedrooms.push(label)
      else this.savedSearchForm.bedrooms.splice(i, 1)
    },

    async createSavedSearch() {
      const email = this.savedSearchForm.email.trim()
      if (!email || !this.selectedUser || this.savingSearch) return
      this.savingSearch = true

      const bedroomCodeMap = { Studio: '0', '4+': '4' }
      const sortedBedrooms = this.bedroomLabels.filter(l => this.savedSearchForm.bedrooms.includes(l))
      const bedroomValues = sortedBedrooms.map(b => bedroomCodeMap[b] || b)
      const actionLabel = (this.actionOptions.find(o => o.value === this.savedSearchForm.action) || {}).text || ''
      const propertyTypeOption = this.savedSearchForm.propertyType
        ? this.propertyTypeOptions.find(o => o.id === this.savedSearchForm.propertyType)
        : null
      const propertyTypeTitle = propertyTypeOption ? propertyTypeOption.name : ''
      const placeName = this.savedSearchForm.placeName.trim()
      const furnitureNames = this.savedSearchForm.furniture
        .map(id => (this.furnitureOptions.find(o => o.id === id) || {}).name)
        .filter(Boolean)
      const zoneNames = this.savedSearchForm.zones
        .map(id => (this.zoneOptions.find(o => o.id === id) || {}).name)
        .filter(Boolean)
      const transitNames = [
        ...this.savedSearchForm.bts.map(id => (this.btsOptions.find(o => o.id === id) || {}).name),
        ...this.savedSearchForm.mrt.map(id => (this.mrtOptions.find(o => o.id === id) || {}).name),
        ...this.savedSearchForm.arl.map(id => (this.arlOptions.find(o => o.id === id) || {}).name),
      ].filter(Boolean)
      const sqmText = (this.savedSearchForm.sqmMin || this.savedSearchForm.sqmMax)
        ? `${this.savedSearchForm.sqmMin || 0}-${this.savedSearchForm.sqmMax || '+'} sqm`
        : ''

      const label = this.savedSearchForm.label.trim() || [
        placeName,
        actionLabel,
        propertyTypeTitle,
        bedroomValues.length ? `${sortedBedrooms.join('/')} BR` : '',
        sqmText,
        this.savedSearchForm.priceMax ? `up to ${this.savedSearchForm.priceMax}` : '',
      ].filter(Boolean).join(' - ')

      const filters = { action_id: this.savedSearchForm.action }
      if (this.savedSearchForm.propertyType) filters.type = this.savedSearchForm.propertyType
      if (bedroomValues.length) filters.bedroom = bedroomValues.join(',')
      if (this.savedSearchForm.priceMin) filters.minPrice = this.savedSearchForm.priceMin
      if (this.savedSearchForm.priceMax) filters.maxPrice = this.savedSearchForm.priceMax
      if (this.savedSearchForm.placeId) filters.place_id = this.savedSearchForm.placeId
      else if (placeName) filters.keyword = placeName
      if (this.savedSearchForm.furniture.length) filters.furniture_ids = this.savedSearchForm.furniture.join(',')
      if (this.savedSearchForm.sqmMin) filters.sqm_min = this.savedSearchForm.sqmMin
      if (this.savedSearchForm.sqmMax) filters.sqm_max = this.savedSearchForm.sqmMax
      if (this.savedSearchForm.zones.length) filters.zones = this.savedSearchForm.zones.join(',')
      if (this.savedSearchForm.bts.length) filters.bts_ids = this.savedSearchForm.bts.join(',')
      if (this.savedSearchForm.mrt.length) filters.mrt_ids = this.savedSearchForm.mrt.join(',')
      if (this.savedSearchForm.arl.length) filters.arl_ids = this.savedSearchForm.arl.join(',')

      try {
        await axios.post('users/v1/saved-search/agent-create', {
          email,
          customerName: this.selectedUser.display_name,
          line_user_id: this.selectedUser.user_id,
          name: label,
          titles: {
            place: placeName,
            action: actionLabel,
            propertyType: propertyTypeTitle,
            bedroom: bedroomValues.join(', '),
            furniture: furnitureNames.join(', '),
            price: this.savedSearchForm.priceMax ? `Up to ${this.savedSearchForm.priceMax}` : '',
            sqm: sqmText,
            zone: zoneNames.join(', '),
            transit: transitNames.join(', '),
          },
          filters,
          path: this.savedSearchForm.action === '2,3' ? '/buy' : '/rent',
        })
        this.$emit('saved', `Saved search created — ${email} will be emailed on new matches`)
        this.view = 'list'
        this.loadList()
      } catch (e) {
        this.$emit('error', 'Failed to save search: ' + (e.response?.data?.message || e.message))
      } finally {
        this.savingSearch = false
      }
    },

    confirmRemove(item) {
      this.$bvModal
        .msgBoxConfirm(`Delete the saved search "${this.displayLabel(item)}"?`, {
          title: 'Please confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) this.removeItem(item.id)
        })
    },

    async removeItem(id) {
      try {
        await axios.delete(`users/v1/saved-search/agent/${id}`, {
          params: { line_user_id: this.selectedUser.user_id },
        })
        this.list = this.list.filter(item => item.id !== id)
        this.$emit('saved', 'Saved search deleted')
      } catch (e) {
        this.$emit('error', 'Failed to delete saved search: ' + (e.response?.data?.message || e.message))
      }
    },

    displayLabel(item) {
      if (typeof item.name === 'string' && item.name) return item.name
      return (item.name && (item.name.en || item.name.th)) || 'Saved search'
    },

    displaySummary(item) {
      const t = item.titles || {}
      return [
        t.place,
        t.action,
        t.propertyType,
        t.bedroom ? `${t.bedroom} BR` : '',
        t.furniture,
        t.sqm,
        t.price,
        t.zone,
        t.transit,
      ].filter(Boolean).join(' · ')
    },

    formatDate(raw) {
      if (!raw) return ''
      const d = new Date(raw)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yy = String(d.getFullYear()).slice(-2)
      return `${dd}/${mm}/${yy}`
    },
  },
}
</script>

<style lang="scss" scoped>
.ss-form-scroll {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
  margin-right: -6px;
}

.ss-suggest-list {
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1060;
  max-height: 180px;
  overflow-y: auto;
}

.ss-transit-tabs ::v-deep .nav-tabs {
  margin-bottom: 0.5rem;
}

.ss-toggle-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ss-toggle-btn {
  border: 1px solid #e0dff5;
  background: transparent;
  color: #6e6b7b;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: #7367f0; color: #7367f0; }

  &--active {
    background: #7367f0;
    border-color: #7367f0;
    color: #fff;
    font-weight: 600;
  }
}

.saved-search-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.saved-search-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
}

.saved-search-item__body {
  min-width: 0;
}

.saved-search-item__label {
  font-weight: 600;
  font-size: 0.92rem;
  color: #3d3d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.saved-search-item__meta {
  font-size: 0.78rem;
  color: #adb5bd;
  margin-top: 2px;
}

.saved-search-item__paused {
  color: #ea5455;
}

.saved-search-item__delete {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;

  &:hover { color: #ea5455; background: #fdecec; }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// v-select dropdowns using append-to-body are moved to <body>, so they
// need a z-index above Bootstrap's modal (1050) to render in front of it.
// vue-select's own base CSS sets z-index: 1000 and gets re-injected by every
// other component that imports this same partial, so !important is required
// to reliably win regardless of <style> tag injection order.
.vs__dropdown-menu {
  z-index: 2000 !important;
}
</style>
